<fin-booking-widget-selector
  class="flex flex-1 grow"
  [label]="(usePopoverSelectors ? 'bookingSummary.paxAmount' : 'passengersAndClass.label') | finLocalization"
  [disabled]="disabled()"
  (click)="openModal()"
  (close)="onClose()"
  data-testid="pax-amount-travel-class-selector"
  [popoverContent]="popoverContent"
  [popoverOptions]="popoverOptions"
  [widgetType]="SelectorType.PAX"
>
  <div class="flex flex--middle">
    <fcom-icon
      class="icon-small"
      [name]="totalPax() > 1 ? SvgLibraryIcon.MULTI_PAX : SvgLibraryIcon.SINGLE_PAX"
      data-testid="pax-amount-selector-icon"
    />
    <span class="overflow-hidden truncate" data-testid="pax-amount-travel-class-text">
      @if (usePopoverSelectors) {
        {{ paxAmountBreakdown() }}
      } @else {
        {{ totalPax() }},
        {{
          'travelClass.' +
            (travelClass() === GlobalBookingTravelClass.MIXED ? 'mixedCombined' : (travelClass() | lowercase))
            | finLocalization
        }}
      }
    </span>
  </div>
</fin-booking-widget-selector>

<ng-template #popoverContent>
  <div class="scroll rounded-large" data-testid="pax-popover-content">
    <header class="popover-header fill relative border-bottom flex--column ps-medium">
      <div class="flex--nogrow flex--basis-header flex flex--row flex--center">
        <h2
          class="flex-1 truncate overflow-hidden nordic-blue-900-text text-icon-primary font-title-medium"
          [attr.id]="'popover-' + popoverOptions.popoverID + '-title'"
        >
          {{ 'bookingSummary.paxAmount' | finLocalization }}
        </h2>
        <button
          class="close-button flex--nogrow flex--self-stretch btn--no-outline focus-inset"
          [attr.title]="'actions.close' | finLocalization"
          [attr.aria-label]="'actions.close' | finLocalization"
          (click)="enableNewSearchAutomatically() ? cancelSelection() : closePopover()"
        >
          <fcom-icon class="icon-medium" [name]="SvgLibraryIcon.CLOSE_DELETE" />
        </button>
      </div>
    </header>
    <ng-container *ngTemplateOutlet="paxAmountTemplate; context: { displayTravelClass: false, popover: true }" />
    <footer class="popover-footer nordic-blue-25-bg surface-secondary ps-medium flex flex--end">
      @if (enableNewSearchAutomatically()) {
        <fcom-button
          data-testid="cancel-button"
          class="ms-small-r"
          [theme]="ButtonTheme.SECONDARY_OUTLINE"
          [text]="'actions.cancel' | finLocalization"
          [aria]="{ label: 'actions.cancel' | finLocalization, role: 'button' }"
          (clickBtn)="cancelSelection()"
        />

        <fcom-button
          data-testid="booking-search-button"
          [theme]="ButtonTheme.SECONDARY_OUTLINE"
          [text]="'bookingSearch.search' | finLocalization"
          [aria]="{ label: 'bookingSearch.search' | finLocalization, role: 'button' }"
          (clickBtn)="startNewSearch()"
          [disabled]="!isSearchEnabled()"
        />
      } @else {
        <fcom-button
          data-testid="done-button"
          [theme]="ButtonTheme.SECONDARY_OUTLINE"
          [text]="'actions.done' | finLocalization"
          [aria]="{ label: 'actions.done' | finLocalization, role: 'button' }"
          (clickBtn)="closePopover()"
        />
      }
    </footer>
  </div>
</ng-template>

@if (modalOpen) {
  <fcom-modal
    [(open)]="modalOpen"
    [title]="'passengersAndClass.label' | finLocalization"
    [fitToContent]="true"
    data-testid="pax-amount-travel-class-modal"
  >
    <ng-container *ngTemplateOutlet="paxAmountTemplate; context: { displayTravelClass: true, popover: false }" />
  </fcom-modal>
}

<ng-template #paxAmountTemplate let-displayTravelClass="displayTravelClass" let-popover="popover">
  <div>
    <ul
      id="pax-details"
      class="flex flex--column"
      tabindex="-1"
      [ngClass]="{
        'pr-large-x': !popover,
        'ps-medium-x': popover,
      }"
    >
      @for (paxItem of paxAmount() | keyvalue; track paxItem.key) {
        <li
          class="flex flex--space-between flex--center fill ps-xsmall-y"
          [attr.data-testid]="'pax-amount-' + paxItem.key"
        >
          <div>
            <div class="capitalize font-body-medium">
              {{
                'bookingWidget.passenger.' + paxItem.key + (paxItem.value <= 1 ? '.singular' : '.plural')
                  | finLocalization
              }}
              {{ 'bookingWidget.passenger.' + paxItem.key + '.ageDefinition' | finLocalization }}
            </div>
            <div class="nordic-blue-900-text text-icon-primary font-body-small">
              {{ 'bookingWidget.passenger.' + paxItem.key + '.ageRuleDefinition' | finLocalization }}
            </div>
          </div>
          @if (paxAmountConfig(); as paxAmountConfig) {
            <fin-plus-minus-selector
              class="fill nordic-blue-900-text text-icon-primary"
              [min]="paxAmountConfig[paxItem.key].min"
              [max]="paxAmountConfig[paxItem.key].max"
              [model]="paxItem.value"
              [decreaseLabel]="'passengerLabels.removeAdult' | finLocalization"
              [increaseLabel]="'passengerLabels.addAdult' | finLocalization"
              [title]="paxItem.value.toString()"
              [iconSize]="'icon-large'"
              [textSize]="'font-headline-small'"
              [buttonPadding]="false"
              [controls]="paxItem.key + '-total-' + paxItem.value"
              (increment)="onPaxDetailsUpdate($event, paxItem.key, AmountUpdateType.INCREMENT)"
              (decrement)="onPaxDetailsUpdate($event, paxItem.key, AmountUpdateType.DECREMENT)"
            />
          }
        </li>
      }
      <fcom-link
        data-testid="child-only-booking-info"
        class="external-link bold-type pr-xsmall-y"
        [href]="'bookingSearch.passenger.childOnly.link.url' | finLocalization"
        [external]="true"
        [openInNewTab]="true"
        [icon]="{ name: SvgLibraryIcon.EXTERNAL_TAB, position: IconPosition.RIGHT }"
        >{{ 'bookingSearch.passenger.childOnly.linkText' | finLocalization }}
      </fcom-link>
    </ul>

    @if (displayTravelClass) {
      <ul id="to-travel-class" role="radiogroup" class="flex flex--column ps-xsmall-y">
        @for (tClass of availableTravelClasses(); track tClass; let first = $first; let last = $last) {
          <li
            [attr.id]="'to-travel-class-' + tClass"
            class="flex flex--middle flex--space-between focus-inset link-hover pr-large-x"
            [class.border-top]="first"
            [class.border-bottom]="!last"
            (click)="onTravelClassSelect(tClass)"
          >
            <input
              type="radio"
              class="is-vishidden"
              name="travelClass"
              [id]="'travelClass-' + tClass"
              [value]="tClass"
              [checked]="travelClass() === tClass"
              [tabindex]="travelClass() === tClass ? 0 : -1"
            />
            <label
              [for]="'travelClass-' + tClass"
              class="fill ps-medium-y focus-inset link-hover nordic-blue-900-text text-icon-primary font-body-medium"
            >
              {{
                'travelClass.' + (tClass === GlobalBookingTravelClass.MIXED ? 'mixedCombined' : (tClass | lowercase))
                  | finLocalization
              }}
            </label>

            @if (travelClass() === tClass) {
              <fcom-icon class="icon-medium nordic-blue-900-text text-icon-primary" [name]="SvgLibraryIcon.CHECKMARK" />
            }
          </li>
        }
      </ul>
    }
  </div>
</ng-template>
